import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAuth,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  workspaceService,
  WorkspaceMember,
} from '@aaas/shared';

export const MembersOnly = () => {
  const { currentWorkspace, user } = useAuth();
  const { t } = useTranslation(['portal', 'shared']);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMembers();
  }, [currentWorkspace?.id, user]);

  const loadMembers = async () => {
    if (!currentWorkspace || !user) return;

    try {
      setLoading(true);
      setError(null);
      const idToken = await user.getIdToken();
      const membersList = await workspaceService.getWorkspaceMembers(currentWorkspace.id, idToken);
      setMembers(membersList);
    } catch (error: unknown) {
      console.error('Failed to load members:', error);
      setError(t('members.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800';
      case 'member':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatJoinDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (!currentWorkspace) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">{t('shared:workspaceSwitcher.none')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle>{t('members.heading')}</CardTitle>
          <CardDescription>
            {t('members.description', { count: members.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2" />
              <span className="text-gray-600">{t('members.loading')}</span>
            </div>
          ) : (
            <div className="space-y-3">
              {members.length > 0 ? (
                members.map((member) => (
                  <div
                    key={member.id}
                    className={`flex items-center space-x-4 p-4 rounded-lg border ${
                      member.is_current_user
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold ${
                        member.is_current_user ? 'bg-blue-600' : 'bg-gray-600'
                      }`}
                    >
                      {member.display_name?.charAt(0) || member.email?.charAt(0) || 'U'}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <p className="font-medium text-gray-900">
                          {member.display_name || member.email || 'Unknown User'}
                        </p>
                        {member.is_current_user && (
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            {t('members.youBadge')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{member.email}</p>
                      <p className="text-xs text-gray-400">
                        {t('members.joinedOn', {
                          defaultValue: 'Joined {{date}}',
                          date: formatJoinDate(member.joined_at),
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <span
                        className={`px-3 py-1 text-sm font-medium rounded-full capitalize ${getRoleColor(member.role)}`}
                      >
                        {t(`shared:workspaceSwitcher.role.${member.role}`, {
                          defaultValue: member.role,
                        })}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <svg
                    className="w-16 h-16 mx-auto mb-4 text-gray-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  <p className="text-lg font-medium mb-2">{t('members.noMembersHeading')}</p>
                  <p className="text-sm">{t('members.noMembersText')}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
